/**
 * 家庭邀請碼的「跨流程攜帶」工具。
 *
 * 使用情境：對方掃 QR / 點邀請連結（`?invite_code=XXXXXX`）後可能：
 *   1. 已是註冊客戶 → 直接走「加入既有的瓦斯帳戶」(addAccount JoinCode) 加入 context。
 *   2. 尚未註冊（LINE login 回 401）→ 會被導去註冊流程，期間 URL query 會遺失。
 *      因此進站時先把邀請碼存到 sessionStorage，註冊送出時再帶進 /line/register，
 *      讓後端「建立 customer + 立即加入 context」一次完成。
 *
 * sessionStorage 可跨 LIFF login 的 redirect round-trip 與 register 內部 navigate 保留。
 */
const KEY = "family_invite_code";

/** 淨化成後端字元集（A-Z2-9，最多 6 碼）；不合法回空字串。 */
const sanitize = (raw: string | null | undefined): string =>
  (raw ?? "").toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 6);

/** 進站時呼叫：若目前 URL 帶 `?invite_code=`，存進 sessionStorage 以跨流程攜帶。 */
export const captureFamilyInviteFromUrl = (): void => {
  if (typeof window === "undefined") return;
  try {
    const raw = new URLSearchParams(window.location.search).get("invite_code");
    const code = sanitize(raw);
    if (code.length === 6) sessionStorage.setItem(KEY, code);
  } catch {
    // 忽略：取不到就當作沒有邀請碼
  }
};

/** 取得已攜帶的家庭邀請碼（6 碼）；沒有則 null。 */
export const getFamilyInviteCode = (): string | null => {
  if (typeof window === "undefined") return null;
  const code = sanitize(sessionStorage.getItem(KEY));
  return code.length === 6 ? code : null;
};

/** 用掉/離開後清除，避免殘留影響後續流程。 */
export const clearFamilyInviteCode = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(KEY);
};
